import {
  DatePicker,
  RegularModal,
  Select,
  TextArea,
  TimePicker,
  UserSelect,
} from '@/components_deprecated'
import { Form, FormField } from '@/components_deprecated/Form'
import { Label } from '@/components_deprecated/Label'
import { notBlankValidation } from '@/utils/forms'
import { useUser } from '@/utils/hooks'
import { useQuery } from '@apollo/client'
import type { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { set } from 'date-fns'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { ALL_ABSENCE_TYPES_QUERY } from '../queries'
import type { CreateAbsenceForm } from '../types'

const GridForm = styled(Form)`
  display: flex;
  flex-direction: column;
  padding: 1rem;

  ${props => props.theme.layout.mobile} {
    display: flex;
    flex-direction: column;
  }

  .submit {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
  }
  & > div + div {
    margin-top: 1rem;
  }
`

const DateEditor = styled.div`
  margin-top: 0.5rem;
  display: flex;

  & > * + * {
    margin-left: 0.5rem;
  }
`

const StyledLabel = styled.label`
  color: inherit;
  font-weight: 700;
  font-size: 0.75rem;
  padding-left: 4px;
`

export interface CreateAbsenceModalProps {
  onSubmit: PromptResolve<CreateAbsenceForm | null>
  admin?: boolean
}

export const CreateAbsenceModal: React.JSX.CFC<CreateAbsenceModalProps> = ({
  onSubmit,
  admin,
}) => {
  const translations = useTranslate({
    selectUser: 'common.select-user',
    absence: 'common.absence',
    absenceType: 'timesheets.absence-type',
    fieldIsRequired: 'error.field-can-not-be-empty',
    selectAbsenceType: 'timesheets.select-absence-type',
    loading: 'common.loading',
    create: 'timesheets.send-request',
    note: 'absences.absence-dialog.labels.reason',
    date: 'common.date',
    newAbsenceRequest: 'absences.absence-dialog.header-add',
    dateFrom: 'common.data-from',
    dateTo: 'common.data-to',
    validation: {
      startBeforeEnd: 'timesheets.error.start-before-end',
    },
  })
  const currentUser = useUser()

  const { data } = useQuery(ALL_ABSENCE_TYPES_QUERY)

  const validateFieldNotEmpty = (errorMessage: string | null) => {
    return (value: string | null) => (value ? null : errorMessage)
  }

  const {
    formValues,
    formErrors,
    submitHandler,
    formChangeHandler: handler,
    updateForm,
  } = useForm<CreateAbsenceForm>({
    values: {
      absenceType: '',
      reason: '',
      dateFrom: new Date(),
      dateTo: new Date(),
      user: currentUser.id,
    },
    validators: {
      absenceType: value => {
        if (value)
          return notBlankValidation(value) ? null : translations.fieldIsRequired
        return translations.fieldIsRequired
      },
      reason: validateFieldNotEmpty(translations.fieldIsRequired),
      dateFrom: (value, form) => {
        if (!value || !form.dateTo) {
          return null
        }
        return value < form.dateTo
          ? null
          : translations.validation.startBeforeEnd
      },
      dateTo: (value, form) => {
        if (!value || !form.dateFrom) {
          return null
        }
        return value > form.dateFrom
          ? null
          : translations.validation.startBeforeEnd
      },
    },
  })

  const absenceTypes = useMemo(() => data?.allAbsenceTypes ?? [], [data])

  const absenceTypeOptions = useMemo(
    () =>
      absenceTypes.map(type => ({
        value: type.id,
        label: type.name,
      })),
    [absenceTypes]
  )

  const handleChangeDate = (field: 'dateFrom' | 'dateTo', date: Date) => {
    const current = formValues[field]
    const newDate = set(date, {
      hours: current.getHours(),
      minutes: current.getMinutes(),
    })
    handler(field)(newDate)
  }

  return (
    <RegularModal
      title={translations.newAbsenceRequest}
      width="500px"
      submitText={translations.create}
      cancelOnClickOutside={true}
      onCancel={() => onSubmit(null)}
      onSubmit={submitHandler(onSubmit)}
    >
      <GridForm preventDefault onSubmit={submitHandler(onSubmit)}>
        {admin && (
          <FormField>
            <Label>{translations.selectUser}</Label>
            <UserSelect
              value={formValues.user ?? null}
              background="quaternary"
              width="100%"
              height="3.875rem"
              onChange={(id, _) => updateForm({ user: id })}
            />
          </FormField>
        )}
        <FormField error={formErrors.dateFrom}>
          <StyledLabel>{translations.dateFrom}</StyledLabel>
          <DateEditor>
            <TimePicker
              value={formValues.dateFrom}
              onChange={handler('dateFrom')}
              grayed={true}
            />
            <DatePicker
              value={formValues.dateFrom}
              onChange={value => handleChangeDate('dateFrom', value)}
              grayed={true}
              fullWidth
            />
          </DateEditor>
        </FormField>
        <FormField error={formErrors.dateTo}>
          <StyledLabel>{translations.dateTo}</StyledLabel>
          <DateEditor>
            <TimePicker
              value={formValues.dateTo}
              onChange={handler('dateTo')}
              grayed={true}
            />
            <DatePicker
              value={formValues.dateTo}
              onChange={value => handleChangeDate('dateTo', value)}
              grayed={true}
              fullWidth
            />
          </DateEditor>
        </FormField>

        <FormField>
          <Label>{translations.absenceType}</Label>
          <Select
            placeholder={translations.selectAbsenceType}
            value={formValues.absenceType}
            fullWidth
            minWidth="280px"
            grayed={true}
            onChange={handler('absenceType')}
            options={absenceTypeOptions}
            error={formErrors.absenceType}
          />
        </FormField>

        <FormField>
          <Label>{translations.note}</Label>
          <TextArea
            value={formValues.reason}
            onChange={handler('reason')}
            width="100%"
            error={formErrors.reason}
          />
        </FormField>
      </GridForm>
    </RegularModal>
  )
}
